import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { Box, Grid, Card, Stack, Switch, TextField, FormControlLabel, Typography, FormHelperText } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../../hooks/useAuth';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
import { UploadAvatar } from '../../../upload';
// utils
import { fData } from '../../../../utils/formatNumber';
//
import countries from '../../user/countries';
import { firestore, storage as st } from '../../../../contexts/FirebaseContext';

// ----------------------------------------------------------------------

export default function AccountGeneral({ user }) {
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  // const { user } = useAuth();

  const updateProfile = async (vals) => {
    await firestore
      .collection('users')
      .doc(user.id)
      .get()
      .then((doc) => {
        if (doc.exists) {
          firestore
            .collection('users')
            .doc(user.id)
            .update({
              ...vals,
              updated_at: new Date()
            });
        } else {
          firestore
            .collection('users')
            .doc(user.id)
            .set({
              ...vals,
              licenciatarioId: null,
              facebookLink: '',
              instagramLink: '',
              linkedinLink: '',
              twitterLink: '',
              cellphoneNum: '',
              whatsphoneNum: '',
              followers: [],
              updated_at: new Date()
            });
        }
      });
  };

  const UpdateUserSchema = Yup.object().shape({
    displayName: Yup.string().required('Nombre es requerido')
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: user.displayName || '',
      position: user.position || '',
      company: user.company || '',
      school: user.school || '',
      email: user.email || '',
      photoURL: user.photoURL || null,
      phoneNumber: user.phoneNumber || '',
      country: user.country || '',
      address: user.address || '',
      state: user.state || '',
      city: user.city || '',
      zipCode: user.zipCode || '',
      about: user.about || '',
      isPublic: user.isPublic || false,
      role: user.role || ''
    },

    validationSchema: UpdateUserSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        await updateProfile({ ...values });
        enqueueSnackbar('Update success', { variant: 'success' });
        if (isMountedRef.current) {
          setSubmitting(false);
        }
      } catch (error) {
        console.log(error);
        if (isMountedRef.current) {
          setErrors({ afterSubmit: error.code });
          setSubmitting(false);
        }
      }
    }
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const format = file.name.split('.')[1];
        const fileT = new File([file], `avatar.${format}`, { type: file.type });
        const uploadAvatar = st.ref().child(`users/${user.id}/avatar.${format}`);
        uploadAvatar.put(fileT).then(() => {
          uploadAvatar.getDownloadURL().then((url) => {
            setFieldValue('photoURL', url);
          });
        });
      }
    },
    [setFieldValue, user]
  );

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ py: 10, px: 3, textAlign: 'center' }}>
              <UploadAvatar
                accept="image/*"
                file={values.photoURL}
                maxSize={3145728}
                onDrop={handleDrop}
                error={Boolean(touched.photoURL && errors.photoURL)}
                caption={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    Permitido *.jpeg, *.jpg, *.png, *.gif
                    <br /> max size of {fData(3145728)}
                  </Typography>
                }
              />

              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {touched.photoURL && errors.photoURL}
              </FormHelperText>

              <FormControlLabel
                control={<Switch {...getFieldProps('isPublic')} color="primary" />}
                labelPlacement="start"
                label="Perfil Publico"
                sx={{ mt: 5 }}
              />
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <Stack spacing={{ xs: 2, md: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Nombre" {...getFieldProps('displayName')} />
                  <TextField fullWidth disabled label="Correo Electronico" {...getFieldProps('email')} />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Numero de Telefono" {...getFieldProps('phoneNumber')} />
                  <TextField fullWidth label="Dirección" {...getFieldProps('address')} />
                </Stack>

                {/* <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    fullWidth
                    label="País"
                    placeholder="País"
                    {...getFieldProps('country')}
                    SelectProps={{ native: true }}
                    error={Boolean(touched.country && errors.country)}
                    helperText={touched.country && errors.country}
                  >
                    <option value="" />
                    {countries.map((option) => (
                      <option key={option.code} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <TextField fullWidth label="Estado/Región" {...getFieldProps('state')} />
                </Stack> */}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Ciudad" {...getFieldProps('city')} />
                  <TextField fullWidth label="Codigo Postal" {...getFieldProps('zipCode')} />
                </Stack>
                {/* <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Posición" {...getFieldProps('position')} />
                  <TextField fullWidth label="Empresa" {...getFieldProps('company')} />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="Escuela" {...getFieldProps('school')} />
                </Stack> */}
                {/* <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField fullWidth label="rol" {...getFieldProps('role')} />
                </Stack> */}
                {/* <TextField {...getFieldProps('about')} fullWidth multiline minRows={4} maxRows={4} label="Sobre ti" /> */}
              </Stack>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                  Guardar Cambios
                </LoadingButton>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
